import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import CountUp from '@/components/base/CountUp';
import ParticleBackground from '@/components/base/ParticleBackground';

const techAdvantages = [
  {
    icon: 'ri-links-line',
    title: '全链路自动化',
    desc: '从剧本生成到成片输出，实现AI全流程自动化生产，大幅缩短内容生产周期，提升产能效率。',
  },
  {
    icon: 'ri-check-double-line',
    title: '标准化品控体系',
    desc: '建立严格的SOP标准化生产流程与品质控制体系，确保每一部作品的画面质量、叙事逻辑和艺术水准。',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: '低成本高效能',
    desc: '相比传统动画制作，AI工业化平台可将生产成本降低80%以上，同时月产能提升至千集级别。',
  },
  {
    icon: 'ri-palette-line',
    title: '文化内容适配性',
    desc: '深度适配中国传统文化元素，AI模型经过大量本土文化素材训练，确保内容的"中国味"和地域特色。',
  },
];

const pipeline = [
  { step: '01', title: '田野采集', desc: '实地采集文化资源' },
  { step: '02', title: '数字归档', desc: '标准化分类与归档' },
  { step: '03', title: '智能分类', desc: 'AI自动标签与索引' },
  { step: '04', title: '云端存储', desc: '安全可靠的云存储' },
  { step: '05', title: '多元复用', desc: '多场景内容创作调用' },
  { step: '06', title: '持续更新', desc: '定期维护与版本迭代' },
];

const stats = [
  { end: 1000, suffix: '+', label: '月产量级/集', duration: 2500 },
  { end: 100, suffix: '+', label: '精品AI漫剧', duration: 2000 },
  { end: 80, suffix: '%', label: '成本降低', duration: 1800 },
  { end: 117, suffix: '', label: '县域数据覆盖', duration: 2200 },
];

export default function Technology() {
  return (
    <div className="ui-page">
      <Navbar />
      <main className="pt-20">
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background-900/40 to-transparent z-[1]"></div>
          <ParticleBackground compact />
          <div className="w-full px-4 md:px-8 lg:px-12 relative z-10">
            <div className="max-w-screen-2xl mx-auto">
              <ScrollReveal>
                <div className="text-center mb-16">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                    <span className="w-1 h-1 rounded-full bg-primary-500 animate-pulse"></span>
                    <span className="text-primary-300 text-xs font-medium tracking-wider whitespace-nowrap">Technology</span>
                  </div>
                  <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-primary-500 mb-6">技术实力</h1>
                  <p className="text-secondary-400 text-lg max-w-2xl mx-auto">自研AI工业化SOP生产标准平台 + 联盟技术中台协同，重构文化内容生产效率</p>
                </div>
              </ScrollReveal>

              <ScrollReveal>
                <div className="bg-background-900/50 rounded-2xl border border-primary-500/15 p-8 md:p-12 mb-16 group hover:border-primary-500/25 transition-all duration-500">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-6">平台总述</h2>
                      <p className="text-secondary-300 text-sm leading-relaxed mb-4">
                        晋铧叙梦自主研发的<strong className="text-foreground-100 font-medium">AI工业化SOP生产标准平台</strong>，是国内领先的AI文化内容工业化生产系统。平台覆盖剧本创作、分镜设计、AI绘画、仿真人制作、剪辑配音到成片输出的全流程，实现文化内容的标准化、规模化、高品质生产。
                      </p>
                      <p className="text-secondary-300 text-sm leading-relaxed">
                        平台支持<strong className="text-primary-500 font-semibold">月产千集级</strong>量产能力，已累计产出100+部精品AI漫剧，内容覆盖文旅推广、品牌故事、红色主旋律、知识科普等多个领域。
                      </p>
                      <p className="text-secondary-300 text-sm leading-relaxed mt-4">
                        作为冯全普·县域AI+OPC联盟核心成员，公司共享联盟AI工具、智能体、算力资源与技术流程，持续迭代技术中台能力。
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {stats.map((item) => (
                        <div
                          key={item.label}
                          className="stat-card bg-background-800/50 rounded-xl border border-primary-500/10 p-6 text-center transition-all duration-500 hover:border-primary-500/30 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(200,160,80,0.06)]"
                        >
                          <div className="font-heading text-3xl font-bold text-primary-500 mb-2">
                            <CountUp end={item.end} duration={item.duration} suffix={item.suffix} />
                          </div>
                          <p className="text-secondary-400 text-xs">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-10 text-center">四大技术优势</h2>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                {techAdvantages.map((item) => (
                  <ScrollReveal key={item.title}>
                    <div className="tech-card group bg-background-900/50 rounded-2xl border border-primary-500/10 p-6 md:p-8 transition-all duration-500 h-full hover:-translate-y-2 hover:border-primary-500/25">
                      <div className="relative">
                        {/* Hover glow */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                          style={{
                            background: 'radial-gradient(circle at 20% 20%, rgba(200,160,80,0.05) 0%, transparent 60%)',
                          }}
                        ></div>

                        <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center mb-5 group-hover:bg-accent-500/20 group-hover:scale-110 transition-all duration-300">
                          <i className={`${item.icon} text-accent-500 text-xl`}></i>
                        </div>
                        <h3 className="text-foreground-50 font-semibold text-base mb-3 group-hover:text-primary-500 transition-colors duration-300">{item.title}</h3>
                        <p className="text-secondary-400 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>

              <ScrollReveal>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-10 text-center">县域文化数字资产底层技术体系</h2>
              </ScrollReveal>
              <div className="bg-background-900/50 rounded-2xl border border-primary-500/15 p-8 md:p-12 hover:border-primary-500/20 transition-all duration-500">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {pipeline.map((item, index) => (
                    <div key={item.step} className="relative text-center group">
                      <div className="w-16 h-16 mx-auto rounded-full bg-primary-500/10 border-2 border-primary-500/30 flex items-center justify-center mb-4 group-hover:bg-primary-500/20 group-hover:border-primary-500/50 group-hover:scale-110 transition-all duration-300">
                        <span className="font-heading text-primary-500 text-xl font-bold">{item.step}</span>
                      </div>
                      <h4 className="text-foreground-50 font-semibold text-sm mb-1">{item.title}</h4>
                      <p className="text-secondary-500 text-xs">{item.desc}</p>
                      {index < pipeline.length - 1 && (
                        <div className="hidden lg:block absolute top-8 -right-2 w-4 text-primary-500/30 group-hover:text-primary-500/50 transition-colors duration-300">
                          <i className="ri-arrow-right-s-line"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-center text-secondary-500 text-xs mt-8">采集 → 归档 → 分类 → 存储 → 复用 → 更新 全流程闭环</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
